import React, { useState, useEffect } from 'react';
import { Outlet, NavLink as RouterNavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { supabase } from '../../config/supabase';
import { FiGrid, FiPackage, FiShoppingCart, FiCreditCard, FiDollarSign, FiTag, FiUsers, FiSettings, FiImage, FiLayers, FiFolder, FiLogOut, FiGlobe, FiTruck } from 'react-icons/fi';
import { FiBell, FiMenu, FiX } from 'react-icons/fi';

const AdminContainer = styled.div`
  display: flex;
  min-height: 100vh;
  height: 100vh;
  background: #f9fafb;
  width: 100%;
  max-width: 100vw;
  overflow-x: clip;
  overflow-y: hidden;
  direction: ${(props) => (props.$isArabic ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.$isArabic ? 'right' : 'left')};
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.$isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 40;
  }
`;

const Sidebar = styled.aside`
  width: 260px;
  background: white;
  border-right: ${(props) => (props.$isArabic ? 'none' : '1px solid #e5e7eb')};
  border-left: ${(props) => (props.$isArabic ? '1px solid #e5e7eb' : 'none')};
  display: flex;
  flex-direction: column;
  direction: ${(props) => (props.$isArabic ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.$isArabic ? 'right' : 'left')};
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 50;

  @media (max-width: 768px) {
    position: fixed;
    left: ${(props) => (props.$isArabic ? 'auto' : '0')};
    right: ${(props) => (props.$isArabic ? '0' : 'auto')};
    top: 0;
    transform: translateX(${(props) => {
      if (props.$isOpen) return '0';
      return props.$isArabic ? '100%' : '-100%';
    }});
    transition: transform 0.3s ease-in-out;
    box-shadow: ${(props) => {
      if (!props.$isOpen) return 'none';
      return props.$isArabic ? '-4px 0 15px rgba(0, 0, 0, 0.1)' : '4px 0 15px rgba(0, 0, 0, 0.1)';
    }};
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
`;

const SidebarHeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoBox = styled.div`
  width: 48px;
  height: 48px;
  background: #5efba9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 18px;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
`;

const UserStatus = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: none;
  margin-left: auto;

  &:hover {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenuButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: none;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Nav = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: 20px 0;
  direction: ${(props) => (props.$isArabic ? 'rtl' : 'ltr')};
`;

const NavLink = styled(RouterNavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: #6b7280;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.2s;
  border-radius: 8px;
  margin: 0 12px;
  
  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }
  
  &.active {
    background: linear-gradient(135deg, #5efba9 0%, #4de89e 100%);
    color: #1f2937;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(94, 251, 169, 0.3);
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100vh;
  overflow-x: visible;
  overflow-y: hidden;
  direction: ${(props) => (props.$isArabic ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.$isArabic ? 'right' : 'left')};

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TopBar = styled.header`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 30;

  @media (max-width: 768px) {
    padding: 12px 16px;
    gap: 8px;
  }
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 15px;
    gap: 6px 8px;
  }
`;

const TitleText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;

  @media (max-width: 768px) {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const SuperAdminBadge = styled.span`
  background: #5efba9;
  color: #1f2937;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 3px 8px;
  }
`;

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    padding: 6px;
    font-size: 18px;
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  background: #5efba9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: white;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserNameTop = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #b91c1c;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 8px 10px;

    span {
      display: none;
    }
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 32px;
  overflow-y: auto;
  overflow-x: visible;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  direction: ${(props) => (props.$isArabic ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.$isArabic ? 'right' : 'left')};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const LanguageSelect = styled.select`
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  transition: all 0.2s;

  &:hover {
    border-color: #5efba9;
  }

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  @media (max-width: 768px) {
    padding: 6px 8px;
    font-size: 12px;
  }
`;

export default function SuperAdminLayout() {
  const { profile, session } = useAuthContext();
  const { language, setLanguage, isArabic } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const userInitials = profile?.first_name?.[0] || session?.user?.email?.[0] || 'S';

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      // Always clear local session first (reliable)
      const { error: localError } = await supabase.auth.signOut();
      if (localError) {
        console.error('Error signing out (local):', localError);
      }

      // Best-effort revoke (non-blocking)
      supabase.auth.signOut({ scope: 'global' }).catch(() => {});

      // Hard redirect so guards reset immediately
      window.location.assign('/superadmin/login');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out. Please try again.');
    } finally {
      setLoggingOut(false);
    }
  };

  // Get page title based on current route
  const getPageTitle = () => {
    if (location.pathname === '/superadmin') return isArabic ? 'لوحة التحكم' : 'Dashboard';
    if (location.pathname === '/superadmin/products') return isArabic ? 'كل المنتجات' : 'All Products';
    if (location.pathname === '/superadmin/orders') return isArabic ? 'كل الطلبات' : 'All Orders';
    if (location.pathname === '/superadmin/users') return isArabic ? 'إدارة المستخدمين' : 'Users Management';
    if (location.pathname === '/superadmin/brands') return isArabic ? 'إدارة العلامات التجارية' : 'Brands Management';
    if (location.pathname === '/superadmin/banners') return isArabic ? 'إدارة البنرات' : 'Banner Management';
    if (location.pathname === '/superadmin/categories') return isArabic ? 'إدارة الفئات' : 'Categories Management';
    if (location.pathname === '/superadmin/subcategories') return isArabic ? 'إدارة الفئات الفرعية' : 'Subcategories Management';
    if (location.pathname === '/superadmin/shipping-countries') return isArabic ? 'دول الشحن' : 'Shipping Countries';
    if (location.pathname === '/superadmin/shipping-rates') return isArabic ? 'أسعار الشحن' : 'Shipping Rates';
    if (location.pathname === '/superadmin/status') return isArabic ? 'الحالة' : 'Status';
    if (location.pathname === '/superadmin/voucher-codes') return isArabic ? 'أكواد الخصم' : 'Voucher Codes';
    if (location.pathname === '/superadmin/payments') return isArabic ? 'المدفوعات' : 'Payments';
    if (location.pathname === '/superadmin/notifications') return isArabic ? 'الإشعارات' : 'Notifications';
    if (location.pathname === '/superadmin/bank-details') return isArabic ? 'التفاصيل المصرفية' : 'Bank Details';
    if (location.pathname === '/superadmin/settings') return isArabic ? 'إعدادات النظام' : 'System Settings';
    return isArabic ? 'لوحة التحكم' : 'Dashboard';
  };

  return (
    <AdminContainer $isArabic={isArabic} dir={isArabic ? 'rtl' : 'ltr'}>
      <Overlay $isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      <Sidebar $isOpen={sidebarOpen} $isArabic={isArabic}>
        <SidebarHeader>
          <SidebarHeaderTop>
            <LogoBox>W</LogoBox>
            <UserInfo>
              <UserName>WASL</UserName>
              <UserStatus>{isArabic ? 'سوبر أدمن' : 'Super Admin'}</UserStatus>
            </UserInfo>
            <MenuButton type="button" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
              <FiX />
            </MenuButton>
          </SidebarHeaderTop>
        </SidebarHeader>
        <Nav $isArabic={isArabic}>
          <NavLink to="/superadmin" end>
            <FiGrid /> {isArabic ? 'لوحة التحكم' : 'Dashboard'}
          </NavLink>
          <NavLink to="/superadmin/products">
            <FiPackage /> {isArabic ? 'كل المنتجات' : 'All Products'}
          </NavLink>
          <NavLink to="/superadmin/status">
            <FiTruck /> {isArabic ? 'الحالة' : 'Status'}
          </NavLink>
          <NavLink to="/superadmin/orders">
            <FiShoppingCart /> {isArabic ? 'كل الطلبات' : 'All Orders'}
          </NavLink>
          <NavLink to="/superadmin/users">
            <FiUsers /> {isArabic ? 'إدارة المستخدمين' : 'Users Management'}
          </NavLink>
          <NavLink to="/superadmin/brands">
            <FiTag /> {isArabic ? 'إدارة العلامات' : 'Brands Management'}
          </NavLink>
          <NavLink to="/superadmin/banners">
            <FiImage /> {isArabic ? 'إدارة البنرات' : 'Banner Management'}
          </NavLink>
          <NavLink to="/superadmin/categories">
            <FiLayers /> {isArabic ? 'الفئات' : 'Categories'}
          </NavLink>
          <NavLink to="/superadmin/subcategories">
            <FiFolder /> {isArabic ? 'الفئات الفرعية' : 'Subcategories'}
          </NavLink>
          <NavLink to="/superadmin/shipping-countries">
            <FiGlobe /> {isArabic ? 'دول الشحن' : 'Shipping Countries'}
          </NavLink>
          <NavLink to="/superadmin/shipping-rates">
            <FiTruck /> {isArabic ? 'أسعار الشحن' : 'Shipping Rates'}
          </NavLink>
          <NavLink to="/superadmin/voucher-codes">
            <FiTag /> {isArabic ? 'أكواد الخصم' : 'Voucher Codes'}
          </NavLink>
          <NavLink to="/superadmin/payments">
            <FiDollarSign /> {isArabic ? 'المدفوعات' : 'Payments'}
          </NavLink>
          <NavLink to="/superadmin/notifications">
            <FiBell /> {isArabic ? 'الإشعارات' : 'Notifications'}
          </NavLink>
          <NavLink to="/superadmin/bank-details">
            <FiCreditCard /> {isArabic ? 'التفاصيل المصرفية' : 'Bank Details'}
          </NavLink>
          <NavLink to="/superadmin/settings">
            <FiSettings /> {isArabic ? 'الإعدادات' : 'Settings'}
          </NavLink>
        </Nav>
      </Sidebar>
      <MainContent $isArabic={isArabic}>
        <TopBar>
          <TopBarLeft>
            <MobileMenuButton type="button" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <FiMenu />
            </MobileMenuButton>
            <PageTitle>
              <TitleText>{getPageTitle()}</TitleText>
              <SuperAdminBadge>{isArabic ? 'سوبر أدمن' : 'Super Admin'}</SuperAdminBadge>
            </PageTitle>
          </TopBarLeft>
          <TopBarActions>
            <IconButton
              type="button"
              aria-label={isArabic ? 'الإشعارات' : 'Notifications'}
              title={isArabic ? 'الإشعارات' : 'Notifications'}
              onClick={() => navigate('/superadmin/notifications')}
            >
              <FiBell />
            </IconButton>
            <LanguageSelect value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="en">EN</option>
              <option value="ar">AR</option>
            </LanguageSelect>
            <UserProfile>
              <Avatar>{userInitials}</Avatar>
              <UserDetails>
                <UserNameTop>{profile?.first_name || (isArabic ? 'سوبر أدمن' : 'Super Admin')}</UserNameTop>
                <UserEmail>{session?.user?.email}</UserEmail>
              </UserDetails>
            </UserProfile>
            <LogoutButton onClick={handleLogout} disabled={loggingOut}>
              <FiLogOut />
              <span>{loggingOut ? (isArabic ? 'جارٍ تسجيل الخروج...' : 'Logging out...') : (isArabic ? 'تسجيل الخروج' : 'Logout')}</span>
            </LogoutButton>
          </TopBarActions>
        </TopBar>
        <Content $isArabic={isArabic}>
          <Outlet />
        </Content>
      </MainContent>
    </AdminContainer>
  );
}

